import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScaleIcon, ShieldCheckIcon, UsersIcon, FileTextIcon, MessageSquareIcon, BookOpenIcon, TargetIcon, LineChartIcon, PieChartIcon } from 'lucide-react';
type TabId = 'policy' | 'content' | 'evaluation';
interface CardData {
  title: string;
  description: string;
  icon: React.ElementType;
  hasChart?: boolean;
}
const tabs: {
  id: TabId;
  label: string;
}[] = [{
  id: 'policy',
  label: 'Public Policy'
}, {
  id: 'content',
  label: 'Content Development'
}, {
  id: 'evaluation',
  label: 'Monitoring & Evaluation'
}];
const tabContent: Record<TabId, CardData[]> = {
  policy: [{
    title: 'Legislative Analysis',
    description: 'Deep analysis of policy frameworks affecting US-Africa trade corridors and bilateral agreements.',
    icon: ScaleIcon
  }, {
    title: 'Regulatory Navigation',
    description: 'Expert guidance through Nigeria and Ghana regulatory environments for market entry compliance.',
    icon: ShieldCheckIcon
  }, {
    title: 'Stakeholder Mapping',
    description: 'Comprehensive mapping of government officials, agencies, and decision-makers across West Africa.',
    icon: UsersIcon
  }],
  content: [{
    title: 'Policy Briefs',
    description: 'Authoritative policy documents that position your organization as a thought leader in African markets.',
    icon: FileTextIcon
  }, {
    title: 'Strategic Communications',
    description: 'Compelling narratives that bridge cultural contexts between US headquarters and African operations.',
    icon: MessageSquareIcon
  }, {
    title: 'Research Publications',
    description: 'Data-driven research reports on market trends, regulatory changes, and investment opportunities.',
    icon: BookOpenIcon
  }],
  evaluation: [{
    title: 'Impact Frameworks',
    description: 'Custom M&E frameworks aligned with international development standards and donor requirements.',
    icon: TargetIcon
  }, {
    title: 'Data Dashboards',
    description: 'Real-time monitoring dashboards tracking KPIs across your African market operations.',
    icon: LineChartIcon,
    hasChart: true
  }, {
    title: 'Program Evaluation',
    description: 'Rigorous evaluation methodologies that demonstrate ROI and social impact to stakeholders.',
    icon: PieChartIcon
  }]
};
export function PracticeAreas() {
  const [activeTab, setActiveTab] = useState<TabId>('policy');
  return <section className="py-20 bg-cream" data-id="element-112">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-113">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-114">
          <h2 className="font-serif text-4xl md:text-5xl text-navy font-bold mb-6" data-id="element-115">
            Our Practice Areas
          </h2>
          <div className="h-1 w-24 bg-gold mx-auto" data-id="element-116"></div>
        </div>

        {/* Tabs */}
        <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8 mb-12 border-b border-gray-200" data-id="element-117">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`pb-4 font-sans font-semibold text-sm uppercase tracking-wide transition-colors relative ${activeTab === tab.id ? 'text-navy' : 'text-gray-400 hover:text-navy/70'}`} data-id="element-118">
              {tab.label}
              {activeTab === tab.id && <motion.div layoutId="activeTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold" initial={false} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-119" />}
            </button>)}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]" data-id="element-120">
          <AnimatePresence mode="wait" data-id="element-121">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-122">
              {tabContent[activeTab].map((card, index) => {
              const Icon = card.icon;
              return <div key={index} className="group bg-white rounded-card p-8 shadow-sm border border-cream-dark hover:shadow-md transition-shadow duration-300 flex flex-col h-full cursor-pointer" data-id="element-123">
                    <div className="mb-6" data-id="element-124">
                      <Icon className="h-8 w-8 text-navy" strokeWidth={1.5} data-id="element-125" />
                    </div>
                    <h3 className="font-serif text-xl text-navy font-semibold mb-4" data-id="element-126">
                      <span className="gold-underline" data-id="element-127">{card.title}</span>
                    </h3>
                    <p className="font-sans text-lg text-text-muted leading-relaxed flex-grow" data-id="element-128">
                      {card.description}
                    </p>

                    {card.hasChart && <div className="mt-6 pt-6 border-t border-gray-100" data-id="element-129">
                        <svg viewBox="0 0 100 40" className="w-full h-12 overflow-visible" data-id="element-130">
                          <motion.path d="M0,40 L0,30 L20,25 L40,28 L60,15 L80,10 L100,5 L100,40 Z" fill="currentColor" className="text-gold/10" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} transition={{
                      delay: 0.2,
                      duration: 0.8
                    }} data-id="element-131" />
                          <motion.path d="M0,30 L20,25 L40,28 L60,15 L80,10 L100,5" fill="none" stroke="currentColor" strokeWidth="2" className="text-gold" initial={{
                      pathLength: 0
                    }} animate={{
                      pathLength: 1
                    }} transition={{
                      delay: 0.2,
                      duration: 1.5,
                      ease: 'easeOut'
                    }} data-id="element-132" />
                          <circle cx="100" cy="5" r="3" className="fill-gold" data-id="element-133" />
                        </svg>
                      </div>}
                  </div>;
            })}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}